/**
 * Gemini chat-model factory for the AI Wedding Planner.
 *
 * Separate from JSON-mode extractJson in @amazing-wedding/ai-core: planner
 * models carry function declarations; search uses google_search in isolation.
 */

import {
  getAI,
  getGenerativeModel,
  GoogleAIBackend,
  HarmBlockThreshold,
  HarmCategory,
  Schema,
  type GenerativeModel,
  type SafetySetting,
  type Tool,
} from "firebase/ai";
import { AiNotConfiguredError, GEMINI_MODEL_FLASH, isConfigError } from "@amazing-wedding/ai-core";
import { getPlannerFirebaseApp } from "./firebase-app";
import { extractGroundingSources } from "./grounding";
import { getToolDeclarations } from "./tools/registry";
import { safeResponseText } from "./response-text";
import type { GroundingSource } from "./types";

const SEARCH_SCOPE_VALUES = ["in_scope", "off_topic"] as const;
export type SearchScope = (typeof SEARCH_SCOPE_VALUES)[number];

const DEFAULT_SAFETY_SETTINGS: SafetySetting[] = [
  {
    category: HarmCategory.HARM_CATEGORY_HARASSMENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
];

const SEARCH_SCOPE_SCHEMA = Schema.enumString({ enum: [...SEARCH_SCOPE_VALUES] });

const SEARCH_CLASSIFIER_SCHEMA = Schema.object({
  properties: {
    scope: SEARCH_SCOPE_SCHEMA,
  },
  required: ["scope"],
});

const SEARCH_CLASSIFIER_INSTRUCTION =
  "Classify whether the user's query is about wedding planning, wedding vendors, wedding traditions, wedding reception or catering for a wedding. " +
  'Reply with JSON {"scope":"off_topic"} for general knowledge unrelated to weddings (recipes, homework, coding, sports, news). ' +
  'Reply with JSON {"scope":"in_scope"} for wedding-industry research or wedding reception planning (including menus for a wedding).';

const WEDDING_SEARCH_INSTRUCTION =
  "Answer using web search. Focus on wedding planning, vendors, traditions, reception and catering for weddings, pricing benchmarks and etiquette. " +
  "Be concise and factual. Include approximate figures and ranges when relevant.";

export type WebSearchResult =
  | { kind: "ok"; summary: string; sources: GroundingSource[] }
  | { kind: "refused" }
  | { kind: "failed"; message: string };

/** Parse enum classifier output; defaults to in_scope when unrecognised. */
export function parseSearchScope(value: string): SearchScope {
  const normalized = value.trim().toLowerCase();
  if (normalized === "off_topic") return "off_topic";
  if (normalized === "in_scope") return "in_scope";
  return "in_scope";
}

function summaryFromSources(sources: GroundingSource[]): string {
  const parts = sources
    .map((s) => s.snippet?.trim() || s.title.trim())
    .filter((part) => part.length > 0);
  return parts.slice(0, 3).join(" ");
}

export function getPlannerModel(systemInstruction: string): GenerativeModel {
  try {
    const ai = getAI(getPlannerFirebaseApp(), { backend: new GoogleAIBackend() });
    const tools: Tool[] = [{ functionDeclarations: getToolDeclarations() }];
    return getGenerativeModel(ai, {
      model: GEMINI_MODEL_FLASH,
      tools,
      systemInstruction,
      safetySettings: DEFAULT_SAFETY_SETTINGS,
    });
  } catch (err) {
    console.error("[ai-planner] getPlannerModel failed:", err);
    throw new AiNotConfiguredError(err);
  }
}

function getSearchClassifierModel(): GenerativeModel {
  const ai = getAI(getPlannerFirebaseApp(), { backend: new GoogleAIBackend() });
  return getGenerativeModel(ai, {
    model: GEMINI_MODEL_FLASH,
    systemInstruction: SEARCH_CLASSIFIER_INSTRUCTION,
    safetySettings: DEFAULT_SAFETY_SETTINGS,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: SEARCH_CLASSIFIER_SCHEMA,
    },
  });
}

function getSearchModel(): GenerativeModel {
  const ai = getAI(getPlannerFirebaseApp(), { backend: new GoogleAIBackend() });
  return getGenerativeModel(ai, {
    model: GEMINI_MODEL_FLASH,
    tools: [{ googleSearch: {} }],
    systemInstruction: WEDDING_SEARCH_INSTRUCTION,
    safetySettings: DEFAULT_SAFETY_SETTINGS,
  });
}

async function classifySearchQuery(query: string): Promise<SearchScope> {
  const model = getSearchClassifierModel();
  const result = await model.generateContent({
    contents: [{ role: "user", parts: [{ text: query }] }],
  });
  const textResult = safeResponseText(result);
  if (!textResult.ok) {
    console.warn("[ai-planner] classifySearchQuery blocked:", textResult.message);
    return "in_scope";
  }

  try {
    const parsed = JSON.parse(textResult.text) as { scope?: string };
    if (typeof parsed.scope === "string") {
      return parseSearchScope(parsed.scope);
    }
  } catch {
    // fall through — model may return bare enum text
  }
  return parseSearchScope(textResult.text);
}

async function executeSearchQuery(query: string): Promise<WebSearchResult> {
  const model = getSearchModel();
  const result = await model.generateContent({
    contents: [{ role: "user", parts: [{ text: query }] }],
  });

  const textResult = safeResponseText(result);
  const sources = extractGroundingSources(result);

  if (!textResult.ok) {
    if (sources.length > 0) {
      const summary = summaryFromSources(sources);
      if (summary) {
        return { kind: "ok", summary, sources };
      }
    }
    return {
      kind: "failed",
      message: textResult.blocked
        ? "Search response was blocked by safety filters."
        : textResult.message,
    };
  }

  const summary = textResult.text.trim();
  if (!summary) {
    if (sources.length > 0) {
      const fallback = summaryFromSources(sources);
      if (fallback) {
        return { kind: "ok", summary: fallback, sources };
      }
    }
    return {
      kind: "failed",
      message: "Search returned no text or sources.",
    };
  }

  return { kind: "ok", summary, sources };
}

export async function runWebSearch(query: string): Promise<WebSearchResult> {
  try {
    let scope: SearchScope;
    try {
      scope = await classifySearchQuery(query);
    } catch (err) {
      console.error("[ai-planner] classifySearchQuery failed:", err);
      scope = "in_scope";
    }

    if (scope === "off_topic") {
      return { kind: "refused" };
    }

    let searchResult = await executeSearchQuery(query);
    if (searchResult.kind === "failed") {
      console.warn("[ai-planner] search_web first attempt failed:", searchResult.message);
      searchResult = await executeSearchQuery(query);
    }
    return searchResult;
  } catch (err) {
    console.error("[ai-planner] runWebSearch failed:", err);
    if (isConfigError(err)) {
      throw new AiNotConfiguredError(err);
    }
    return {
      kind: "failed",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
